import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert, Paper, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Avatar, Fade, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Menu, MenuItem, ListItemIcon, ListItemText,
    useTheme
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack,
    Add as AddIcon,
    Description as DescriptionIcon,
    Warning as WarningIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';

const ModulosGerenciamentoPagina = () => {
    const [modulos, setModulos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [dialogoConfirmacao, setDialogoConfirmacao] = useState({
        aberto: false,
        moduloId: null,
        nomeModulo: ''
    });
    const [menuAnchorEl, setMenuAnchorEl] = useState({
        id: null,
        element: null
    });
    const navegar = useNavigate();
    const theme = useTheme();

    const buscarModulos = async () => {
        try {
            const resposta = await api.get('/api/modulos');
            setModulos(resposta.data);
        } catch (err) {
            setErro('Falha ao carregar os módulos.');
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        buscarModulos();
    }, []);

    const abrirMenuAcoes = (event, moduloId) => {
        setMenuAnchorEl({
            id: moduloId,
            element: event.currentTarget
        });
    };

    const fecharMenuAcoes = () => {
        setMenuAnchorEl({
            id: null,
            element: null
        });
    };

    const abrirDialogoConfirmacao = (moduloId, nomeModulo) => {
        setDialogoConfirmacao({
            aberto: true,
            moduloId,
            nomeModulo
        });
    };

    const fecharDialogoConfirmacao = () => {
        setDialogoConfirmacao({
            aberto: false,
            moduloId: null,
            nomeModulo: ''
        });
    };

    const confirmarDelecao = async () => {
        try {
            await api.delete(`/api/modulos/${dialogoConfirmacao.moduloId}`);
            setModulos(modulosAtuais => modulosAtuais.filter(modulo => modulo.id !== dialogoConfirmacao.moduloId));
            fecharDialogoConfirmacao();
        } catch (err) {
            setErro(err.response?.data?.erro || "Erro ao deletar o módulo.");
            fecharDialogoConfirmacao();
        }
    };

    const formatarData = (dataString) => {
        if (!dataString) return '';
        const partes = dataString.split('T')[0].split('-');
        if (partes.length !== 3) return 'Data inválida';
        const [ano, mes, dia] = partes;
        return `${dia}/${mes}/${ano}`;
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 4,
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    background: cores.gradientes.primario,
                    color: 'white'
                }}>
                    <IconButton
                        onClick={() => navegar('/gerenciar')}
                        sx={{
                            mr: 2,
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                            }
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                lineHeight: 1.2
                            }}
                        >
                            Gerenciamento de Módulos
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            Visualize, edite e crie novos módulos para o curso
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                }}>
                    <Box>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                color: cores.primaria.principal,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}
                        >
                            Lista de Módulos
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {modulos.length} {modulos.length === 1 ? 'módulo encontrado' : 'módulos encontrados'}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        component={RouterLink}
                        to="/modulos/criar"
                        startIcon={<AddIcon />}
                        sx={{
                            bgcolor: cores.primaria.principal,
                            borderRadius: 2,
                            boxShadow: cores.sombras.suave,
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            }
                        }}
                    >
                        Criar Novo Módulo
                    </Button>
                </Box>

                {erro && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            backgroundColor: cores.erro.claro,
                            borderColor: cores.erro.medio,
                            color: cores.erro.escuro,
                            '& .MuiAlert-icon': {
                                alignItems: 'center'
                            }
                        }}
                    >
                        {erro}
                    </Alert>
                )}

                <Fade in={true} timeout={500}>
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 2,
                            boxShadow: cores.sombras.suave,
                            overflow: 'hidden',
                            border: `1px solid ${cores.cinza.claro}`,
                        }}
                    >
                        <Table>
                            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Nome do Módulo</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Data de Início</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Data de Fim</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {modulos.map((modulo, index) => (
                                    <Fade in={true} timeout={300 + index * 100} key={modulo.id}>
                                        <TableRow
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: '#f8fafc',
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Box
                                                    component={RouterLink}
                                                    to={`/modulos/${modulo.id}`}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        textDecoration: 'none',
                                                        color: 'inherit',
                                                        '&:hover': {
                                                            color: cores.primaria.principal,
                                                        }
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            mr: 2,
                                                            bgcolor: 'transparent',
                                                            color: cores.primaria.principal,
                                                            border: `2px solid ${cores.cinza.claro}`,
                                                            width: 36,
                                                            height: 36
                                                        }}
                                                    >
                                                        <DescriptionIcon />
                                                    </Avatar>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '1rem'
                                                        }}
                                                    >
                                                        {modulo.nome}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        color: cores.cinza.escuro,
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {formatarData(modulo.data_inicio)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        color: cores.cinza.escuro,
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {formatarData(modulo.data_fim)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    onClick={(e) => abrirMenuAcoes(e, modulo.id)}
                                                    size="small"
                                                    sx={{
                                                        border: `1px solid ${cores.cinza.claro}`,
                                                        '&:hover': {
                                                            bgcolor: '#f1f5f9',
                                                        }
                                                    }}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    </Fade>
                                ))}
                                {modulos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                Nenhum módulo encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Fade>
            </Paper>

            {/* Diálogo de Confirmação */}
            <Dialog
                open={dialogoConfirmacao.aberto}
                onClose={fecharDialogoConfirmacao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a excluir o módulo <strong>{dialogoConfirmacao.nomeModulo}</strong>. Esta ação não poderá ser desfeita e todos os dados relacionados a este módulo serão perdidos.
                    </DialogContentText>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        Todas as turmas e avaliações associadas a este módulo serão afetadas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharDialogoConfirmacao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmarDelecao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Excluir Módulo
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={menuAnchorEl.element}
                open={Boolean(menuAnchorEl.element)}
                onClose={fecharMenuAcoes}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        boxShadow: cores.sombras.medio,
                        '& .MuiList-root': {
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem
                    component={RouterLink}
                    to={`/modulos/${menuAnchorEl.id}`}
                    onClick={fecharMenuAcoes}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <DescriptionIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Ver Detalhes</ListItemText>
                </MenuItem>
                <MenuItem
                    component={RouterLink}
                    to={`/modulos/editar/${menuAnchorEl.id}`}
                    onClick={fecharMenuAcoes}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const modulo = modulos.find(m => m.id === menuAnchorEl.id);
                        abrirDialogoConfirmacao(menuAnchorEl.id, modulo?.nome || '');
                        fecharMenuAcoes();
                    }}
                    sx={{
                        py: 1,
                        color: cores.erro.principal
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: cores.erro.principal }} />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default ModulosGerenciamentoPagina;